import { useMemo } from "react";
import { useBulkAppointmentAvailability } from "./useBulkAppointmentAvailability";
import { IEmployeeAvailability } from "../models/IEmployee";

/**
 * Hook to find the first available day across all employees for "any employee" selection
 * Uses individual API calls for each employee (up to 5) to avoid React hooks rules
 */
export const useAnyEmployeeFirstAvailableDay = (
  employees: IEmployeeAvailability[],
  serviceId: number | undefined,
  studioId: number | undefined
) => {
  const tomorrow = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date;
  }, []);

  // Filter out the "any employee" option (id: -1) to get real employees
  const realEmployees = useMemo(
    () => employees.filter((emp) => emp.id !== -1),
    [employees]
  );

  // Fetch bulk availability for each real employee
  // Note: We need to use hooks at the top level, so we'll fetch for a limited number of employees
  const employee1 = realEmployees[0];
  const employee2 = realEmployees[1];
  const employee3 = realEmployees[2];
  const employee4 = realEmployees[3];
  const employee5 = realEmployees[4];

  const emp1Availability = useBulkAppointmentAvailability(
    employee1?.id,
    serviceId,
    tomorrow,
    studioId
  );

  const emp2Availability = useBulkAppointmentAvailability(
    employee2?.id,
    serviceId,
    tomorrow,
    studioId
  );

  const emp3Availability = useBulkAppointmentAvailability(
    employee3?.id,
    serviceId,
    tomorrow,
    studioId
  );

  const emp4Availability = useBulkAppointmentAvailability(
    employee4?.id,
    serviceId,
    tomorrow,
    studioId
  );

  const emp5Availability = useBulkAppointmentAvailability(
    employee5?.id,
    serviceId,
    tomorrow,
    studioId
  );

  const employeeAvailabilities = useMemo(() => {
    const availabilities = [];

    if (employee1) {
      availabilities.push({
        employeeId: employee1.id,
        employee: employee1,
        dateAvailabilityMap: emp1Availability.dateAvailabilityMap,
        hasData: emp1Availability.hasData,
        isLoading: emp1Availability.isLoading,
      });
    }

    if (employee2) {
      availabilities.push({
        employeeId: employee2.id,
        employee: employee2,
        dateAvailabilityMap: emp2Availability.dateAvailabilityMap,
        hasData: emp2Availability.hasData,
        isLoading: emp2Availability.isLoading,
      });
    }

    if (employee3) {
      availabilities.push({
        employeeId: employee3.id,
        employee: employee3,
        dateAvailabilityMap: emp3Availability.dateAvailabilityMap,
        hasData: emp3Availability.hasData,
        isLoading: emp3Availability.isLoading,
      });
    }

    if (employee4) {
      availabilities.push({
        employeeId: employee4.id,
        employee: employee4,
        dateAvailabilityMap: emp4Availability.dateAvailabilityMap,
        hasData: emp4Availability.hasData,
        isLoading: emp4Availability.isLoading,
      });
    }

    if (employee5) {
      availabilities.push({
        employeeId: employee5.id,
        employee: employee5,
        dateAvailabilityMap: emp5Availability.dateAvailabilityMap,
        hasData: emp5Availability.hasData,
        isLoading: emp5Availability.isLoading,
      });
    }

    return availabilities;
  }, [
    employee1,
    employee2,
    employee3,
    employee4,
    employee5,
    emp1Availability,
    emp2Availability,
    emp3Availability,
    emp4Availability,
    emp5Availability,
  ]);

  // Check if all employee availability data has loaded
  const isLoadingAny = employeeAvailabilities.some((emp) => emp.isLoading);
  const hasDataAll = employeeAvailabilities.every((emp) => emp.hasData);

  // Find the earliest available day across all employees
  const firstAvailableResult = useMemo((): {
    firstAvailableDate: Date | null;
    selectedEmployee: IEmployeeAvailability | null;
    hasAnyAvailability: boolean;
  } => {
    if (isLoadingAny || !hasDataAll || realEmployees.length === 0) {
      return {
        firstAvailableDate: null,
        selectedEmployee: null,
        hasAnyAvailability: false,
      };
    }

    // Collect all unique date keys from all employee availability maps and sort them
    const allDateKeys = new Set<string>();
    for (const empAvailability of employeeAvailabilities) {
      for (const dateKey of empAvailability.dateAvailabilityMap.keys()) {
        allDateKeys.add(dateKey);
      }
    }
    const sortedDateKeys = Array.from(allDateKeys).sort();
    const todayKey = new Date().toISOString().split("T")[0];

    // Check each day in sorted order (days returned by backend based on visibility settings)
    for (const dateKey of sortedDateKeys) {
      // Skip today, start from tomorrow
      if (dateKey <= todayKey) continue;

      // Check each employee for availability on this date
      for (const empAvailability of employeeAvailabilities) {
        const dateAvailability =
          empAvailability.dateAvailabilityMap.get(dateKey);

        if (
          dateAvailability?.hasSlots &&
          dateAvailability.timeSlots.some((slot) => slot.isAvailable)
        ) {
          // Found the first available day with an available employee
          const checkDate = new Date(dateKey + "T12:00:00");
          return {
            firstAvailableDate: checkDate,
            selectedEmployee: empAvailability.employee,
            hasAnyAvailability: true,
          };
        }
      }
    }

    // No availability found in the visible days
    return {
      firstAvailableDate: null,
      selectedEmployee: null,
      hasAnyAvailability: false,
    };
  }, [isLoadingAny, hasDataAll, realEmployees, employeeAvailabilities]);

  return {
    firstAvailableDate: firstAvailableResult.firstAvailableDate,
    selectedEmployee: firstAvailableResult.selectedEmployee,
    hasAnyAvailability: firstAvailableResult.hasAnyAvailability,
    isLoading: isLoadingAny,
    hasData: hasDataAll,
  };
};
